import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import departureApi from '../services/departureApi';

// Stable empty report so consumers never crash before the first fetch / on error
// (mirrors EMPTY_REPORT in useArrivalReadiness.js).
//
// ⚠️ headline defaults to 'unverified', NOT 'ready'. The whole point of this surface is that it
// never shows a green it hasn't earned — a pre-fetch or errored state must not read as "everyone
// got their reminder".
const EMPTY_REPORT = {
  headline: 'unverified',
  dayKey: null,
  count: 0,
  summary: {
    departures: 0, action: 0, unverified: 0, ready: 0, pending: 0, na: 0, manualPending: 0, checkoutRecorded: 0
  },
  departures: []
};

export const DEPARTURE_READINESS_QUERY_KEY = ['departure-readiness-today'];

// Today's departure readiness for the admin dashboard card
// (tasks/checkout-message-automation.md §3.9): one row per guest checking out today, with the
// reminder status. Server recomputes on each fetch from the sender's own eligibility rulebook.
//
// The 5-min refresh matters more here than on the arrivals card: the send window is only 20 minutes
// wide, so a row moves from "pending" to "sent" (or to a permanent miss) inside a single sitting.
export const useDepartureReadiness = () => {
  const result = useQuery({
    queryKey: DEPARTURE_READINESS_QUERY_KEY,
    queryFn: () => departureApi.getTodayDepartureReadiness(),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000
  });

  return {
    ...result,
    report: result.data ?? EMPTY_REPORT
  };
};

/**
 * Acknowledge that a Gathern / manual / direct booking's checkout reminder was sent by hand.
 *
 * Invalidates the readiness query rather than patching the row locally: the reminder column is a
 * SERVER verdict, and a client-side guess at the new state is exactly how a surface starts
 * disagreeing with the report it is supposed to mirror.
 */
export const useMarkCheckoutMessageSentManually = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: departureApi.markCheckoutMessageSentManually,
    // onSettled, not onSuccess: a refusal here means the client's view was STALE — the row offered a
    // tick for a booking the server says is automated — so the failure case is precisely when a
    // refetch is most needed. Invalidating only on success would leave the bad row on screen.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DEPARTURE_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to mark checkout reminder as sent manually:', error);
    }
  });
};

export const useUndoCheckoutMessageSentManually = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: departureApi.undoCheckoutMessageSentManually,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DEPARTURE_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to undo the manual-send acknowledgement:', error);
    }
  });
};

/**
 * Record that operations confirmed this guest had left, at a stated time
 * (tasks/manual-guest-checkout-event.md).
 *
 * Same invalidate-don't-patch rule as the manual-send mutations above: the checkout column is a
 * SERVER verdict, and a client-side guess is how a surface starts disagreeing with its own report.
 *
 * @param {{bookingId: string, observedAt: string}} vars
 */
export const useRecordGuestCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, observedAt }) => departureApi.recordGuestCheckout(bookingId, observedAt),
    // onSettled, not onSuccess: a refusal here (a cancelled booking, an out-of-bounds time) means
    // the row on screen was stale, which is exactly when a refetch matters most.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DEPARTURE_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to record the guest checkout:', error);
    }
  });
};

export const useUndoGuestCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // ⚠️ Takes `{bookingId, source}`, not a bare id. A booking can carry an ops record AND a guest's
    // own plaque report, and the endpoint withdraws the one it is named — so the caller must say
    // which record is on screen. Passing the id alone silently targets the ops record.
    mutationFn: ({ bookingId, source }) => departureApi.undoGuestCheckout(bookingId, source),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: DEPARTURE_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to undo the checkout record:', error);
    }
  });
};
